import type { ForecastingRow } from '@/api/forecasting';
import type { MetricField } from '../forecasting.types';
import type {
  BuyingPointComparisonRow,
  BuyingPointSummaryRow,
  CampaignDetailRow,
  MonthlyGroupedRow,
  QuarterTotals
} from '../threeMonth.types';
import {
  getDynamicForecastGp,
  getDynamicForecastRevenue,
  getTargetGp,
  getTargetRevenue,
  isDynamicForecastStage
} from './dynamicForecastMetrics';
import { getDrillLevelConfig } from './drilldownTable';
import { monthSequenceFromRange } from './forecastingAggregations';
import { parseMetricNumber } from './forecastingNumbers';
import { formatMonthAxisLabel, monthKeyFromDate, parseRowMonthDate } from './forecastingDates';
import { isBookedStatus, isPipeStatus } from './monthlyStageRules';

function isPrePipeStatus(status?: string): boolean {
  if (isPipeStatus(status) || isBookedStatus(status)) return false;
  const t = status?.trim().toLowerCase() ?? '';
  if (!t) return false;
  if (t.includes('pre') && t.includes('pipe')) return true;
  const pct = t.match(/^(\d+)%/);
  if (pct) {
    const n = Number(pct[1]);
    return n > 0 && n < 70;
  }
  return false;
}

/* Booked is booked whatever the month or year: counted on Status alone. The
   compiled sheet labels Forecast_Stage by how far out a month is -- booked
   rows are "Dynamic Forecast" this month and next, but "Booked" beyond that
   -- so gating on the stage dropped every booking two or more months out
   (Dec 2026 showed nothing booked in October). Pipe is counted the same way,
   so months beyond next show their pipe too. The dynamic forecast keeps the
   stage rule: that is where it means what it says. */
function bookedRevenue(row: ForecastingRow): number {
  if (!isBookedStatus(row.Status)) return 0;
  return parseMetricNumber(row.Net_Rev__Rec_);
}

function pipeRevenue(row: ForecastingRow): number {
  if (!isPipeStatus(row.Status)) return 0;
  return parseMetricNumber(row.Net_Rev__Rec_);
}

/* Counted on Status alone, like booked and pipe: a 30% or 50% row is never in
   the dynamic forecast, so gating on the stage left the column empty and the
   deal table blank when filtered to those statuses. */
function prePipeRevenue(row: ForecastingRow): number {
  if (!isPrePipeStatus(row.Status)) return 0;
  return parseMetricNumber(row.Net_Rev__Rec_);
}

/* The quarterly page is the sales team's view, so it measures against their
   Target rows, not the Budgetv2 budget the full-year page shows. */
function targetRevenue(row: ForecastingRow): number {
  return getTargetRevenue(row);
}

function targetGp(row: ForecastingRow): number {
  return getTargetGp(row);
}

/**
 * The quarterly scorecards. The same shape as the full-year totals, with the
 * goal figures taken from Target rather than Budget — the budget* keys hold
 * Target here, so the shared lens names still select them.
 */
export function computeQuarterTotals(rows: ForecastingRow[]): QuarterTotals {
  let booked = 0;
  let pipe = 0;
  let dynamicRev = 0;
  let dynamicGpTotal = 0;
  let targetRev = 0;
  let targetGpTotal = 0;
  for (const row of rows) {
    booked += bookedRevenue(row);
    pipe += pipeRevenue(row);
    dynamicRev += dynamicRevenue(row);
    dynamicGpTotal += dynamicGp(row);
    targetRev += targetRevenue(row);
    targetGpTotal += targetGp(row);
  }
  return {
    booked,
    pipe,
    dynamicRevenue: dynamicRev,
    dynamicGp: dynamicGpTotal,
    budgetRevenue: targetRev,
    budgetGp: targetGpTotal,
    vsBudgetRevenuePct: targetRev > 0 ? (dynamicRev / targetRev) * 100 : null,
    vsBudgetGpPct: targetGpTotal > 0 ? (dynamicGpTotal / targetGpTotal) * 100 : null
  };
}

function dynamicRevenue(row: ForecastingRow): number {
  return getDynamicForecastRevenue(row);
}

function dynamicGp(row: ForecastingRow): number {
  return getDynamicForecastGp(row);
}

function marginPct(revenue: number, gp: number): number {
  return revenue > 0 ? (gp / revenue) * 100 : 0;
}

export function groupByMonthGrouped(
  rows: ForecastingRow[],
  dateFrom: string,
  dateTo: string
): MonthlyGroupedRow[] {
  const seq = monthSequenceFromRange(dateFrom, dateTo);
  const map = new Map<string, MonthlyGroupedRow>();

  for (const { key, axisLabel } of seq) {
    map.set(key, {
      monthKey: key,
      axisLabel,
      BookedPlusPipe: 0,
      DynamicForecast: 0,
      Target: 0
    });
  }

  for (const row of rows) {
    const md = parseRowMonthDate(row);
    if (!md) continue;
    const key = monthKeyFromDate(md);
    const bucket = map.get(key);
    if (!bucket) continue;

    // One bar for both: the table below keeps them apart.
    bucket.BookedPlusPipe += bookedRevenue(row) + pipeRevenue(row);
    bucket.DynamicForecast += dynamicRevenue(row);
    bucket.Target += targetRevenue(row);
  }

  return seq.map(({ key }) => map.get(key)!);
}

/**
 * Rows for one month of the quarter, by the `YYYY-MM` key the monthly chart
 * uses. Picking a month on the chart narrows the rest of the page to it, as
 * Looker Studio's cross-filtering did; without a month, every row is kept.
 */
export function filterRowsByMonth(rows: ForecastingRow[], monthKey: string | null): ForecastingRow[] {
  if (!monthKey) return rows;
  return rows.filter((row) => {
    const md = parseRowMonthDate(row);
    return md != null && monthKeyFromDate(md) === monthKey;
  });
}

export function groupBuyingPointSummary(rows: ForecastingRow[]): BuyingPointSummaryRow[] {
  return groupDrillSummary(rows, 0);
}

function dimensionValue(row: ForecastingRow, field: keyof ForecastingRow): string {
  const raw = row[field];
  if (raw == null || raw === '') return 'Unknown';
  return String(raw).trim() || 'Unknown';
}

export function groupDrillSummary(rows: ForecastingRow[], levelIndex: number): BuyingPointSummaryRow[] {
  const level = getDrillLevelConfig(levelIndex);
  const map = new Map<
    string,
    {
      booked: number;
      pipe: number;
      dynamicRev: number;
      dynamicGp: number;
      targetRev: number;
      targetGp: number;
    }
  >();

  for (const row of rows) {
    const dim = dimensionValue(row, level.field);
    const cur = map.get(dim) ?? {
      booked: 0,
      pipe: 0,
      dynamicRev: 0,
      dynamicGp: 0,
      targetRev: 0,
      targetGp: 0
    };
    cur.booked += bookedRevenue(row);
    cur.pipe += pipeRevenue(row);
    cur.dynamicRev += dynamicRevenue(row);
    cur.dynamicGp += dynamicGp(row);
    cur.targetRev += targetRevenue(row);
    cur.targetGp += targetGp(row);
    map.set(dim, cur);
  }

  return [...map.entries()]
    .map(([label, v]) => ({
      key: `${level.level}:${label}`,
      label,
      buyingPoint: label,
      booked: v.booked,
      pipe: v.pipe,
      bookedPlusPipe: v.booked + v.pipe,
      dynamicForecast: v.dynamicRev,
      target: v.targetRev,
      vsTargetRevPct: v.targetRev > 0 ? (v.dynamicRev / v.targetRev) * 100 : null,
      vsTargetGpPct: v.targetGp > 0 ? (v.dynamicGp / v.targetGp) * 100 : null
    }))
    .sort((a, b) => b.dynamicForecast - a.dynamicForecast);
}

export function groupBuyingPointComparison(
  rows: ForecastingRow[],
  metric: MetricField,
  levelIndex = 0
): BuyingPointComparisonRow[] {
  return groupDrillComparison(rows, metric, levelIndex);
}

export function groupDrillComparison(
  rows: ForecastingRow[],
  metric: MetricField,
  levelIndex: number
): BuyingPointComparisonRow[] {
  const level = getDrillLevelConfig(levelIndex);
  const map = new Map<string, { dynamicForecast: number; target: number }>();

  for (const row of rows) {
    const name = dimensionValue(row, level.field);
    const cur = map.get(name) ?? { dynamicForecast: 0, target: 0 };
    if (metric === 'gp') {
      cur.dynamicForecast += dynamicGp(row);
      cur.target += targetGp(row);
    } else {
      cur.dynamicForecast += dynamicRevenue(row);
      cur.target += targetRevenue(row);
    }
    map.set(name, cur);
  }

  return [...map.entries()]
    .map(([name, v]) => ({ name, ...v }))
    .filter((r) => r.dynamicForecast > 0 || r.target > 0)
    .sort((a, b) => b.dynamicForecast - a.dynamicForecast)
    .slice(0, 12);
}

export function buildCampaignDetailRows(rows: ForecastingRow[]): CampaignDetailRow[] {
  return rows
    // Booked, pipe and pre-pipe rows outside the dynamic forecast still belong in the detail.
    .filter(
      (row) =>
        isDynamicForecastStage(row.Forecast_Stage) ||
        isBookedStatus(row.Status) ||
        isPipeStatus(row.Status) ||
        isPrePipeStatus(row.Status)
    )
    .map((row) => {
      const booked = bookedRevenue(row);
      const pipe = pipeRevenue(row);
      const prePipe = prePipeRevenue(row);
      /* A deal listed for its status shows its own GP and margin. Taking them
         from the dynamic forecast gave every deal outside it -- 50% proposals,
         bookings staged "Booked" further out -- GP of £0 and a 0% margin. */
      const stageRev = booked + pipe + prePipe;
      const rev = stageRev > 0 ? stageRev : dynamicRevenue(row);
      const gp = stageRev > 0 ? parseMetricNumber(row.Gross_Profit) : dynamicGp(row);

      return {
        key: `${row.Campaign_ID}-${row.Line_Item_ID}-${row.Month__Date_}`,
        campaignId: row.Campaign_ID || '—',
        campaignName: row.Campaign?.trim() || '—',
        buyingPoint: row.Buying_Point?.trim() || '—',
        advertiser: row.Advertiser?.trim() || '—',
        status: row.Status?.trim() || '—',
        startDate: row.Start_Date?.trim() || '—',
        endDate: row.End_Date?.trim() || '—',
        booked,
        pipe,
        prePipe,
        gp,
        margin: marginPct(rev, gp)
      };
    })
    .filter((r) => r.booked > 0 || r.pipe > 0 || r.prePipe > 0 || r.gp > 0)
    .sort((a, b) => b.booked + b.pipe - (a.booked + a.pipe));
}

export function formatShortDateLabel(iso: string): string {
  if (!iso || iso === '—') return iso;
  const md = parseRowMonthDate({ Month__Date_: iso } as ForecastingRow);
  if (md) return formatMonthAxisLabel(md);
  return iso;
}
